import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { AlertCircle, CheckCircle2, ChevronDown, ImagePlus, LifeBuoy, Loader2, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_META,
  TICKET_STATUS_META,
  TICKET_TYPE_META,
  TICKET_TYPES,
  createTicket,
  getTicket,
  isEmptyTicketHtml,
  listTickets,
  replyToTicket,
  sanitizeTicketHtml,
  ticketLooksLikeHtml,
  type CockpitTicket,
  type TicketPriority,
  type TicketReply,
  type TicketScreenshot,
  type TicketStatus,
  type TicketType,
} from '../lib/cockpitTickets';
import { TicketDetailsEditor } from './TicketDetailsEditor';
import {
  MAX_TICKET_SCREENSHOTS,
  collectImageFiles,
  fileToScreenshotDraft,
  revokeScreenshotUrls,
  type TicketScreenshotDraft,
} from '../lib/ticketScreenshots';

type Tab = 'new' | 'history';

function isTicketStatus(value: string): value is TicketStatus {
  return value in TICKET_STATUS_META;
}

function isTicketType(value: string | null | undefined): value is TicketType {
  return !!value && value in TICKET_TYPE_META;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusMeta(status: string) {
  return isTicketStatus(status)
    ? TICKET_STATUS_META[status]
    : { label: status, className: 'bg-slate-100 text-slate-700' };
}

function typeLabel(type: string | null | undefined): string | null {
  return isTicketType(type) ? TICKET_TYPE_META[type].label : null;
}

function attachmentsNotInBody(description: string, shots: TicketScreenshot[]): TicketScreenshot[] {
  const inline = new Set<string>();
  for (const match of description.matchAll(/\ssrc=["']([^"']+)["']/gi)) {
    inline.add(match[1]);
  }
  return shots.filter((shot) => !inline.has(shot.url));
}

function HistoryItem({
  ticket,
  selected,
  onSelect,
}: {
  ticket: CockpitTicket;
  selected: boolean;
  onSelect: () => void;
}) {
  const status = statusMeta(ticket.status);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={selected}
      className={[
        'w-full px-3.5 py-3 text-left transition-colors',
        selected
          ? 'bg-action-surface'
          : 'bg-white hover:bg-slate-50/80',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action/35',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-sm font-semibold tracking-tight text-slate-900">{ticket.title}</p>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.className}`}>
            {status.label}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-slate-400 transition-transform duration-150 ${selected ? 'rotate-180 text-action' : ''}`}
            aria-hidden
          />
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {typeLabel(ticket.ticket_type) ?? 'Support request'} · {formatDate(ticket.created_at)}
      </p>
    </button>
  );
}

function ScreenshotGallery({ shots }: { shots: TicketScreenshot[] }) {
  if (shots.length === 0) return null;
  return (
    <ul className="grid grid-cols-2 gap-2">
      {shots.map((shot) => (
        <li key={shot.url}>
          <a
            href={shot.url}
            target="_blank"
            rel="noreferrer"
            className="block overflow-hidden rounded-lg border border-slate-200 bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35"
          >
            <img src={shot.url} alt={shot.name} className="h-28 w-full object-cover" />
            <span className="block truncate px-2 py-1 text-[11px] text-slate-500">{shot.name}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function TicketBody({ value }: { value: string }) {
  if (ticketLooksLikeHtml(value)) {
    return (
      <div
        className="ticket-rich-text text-sm leading-relaxed text-slate-700"
        dangerouslySetInnerHTML={{ __html: sanitizeTicketHtml(value) }}
      />
    );
  }
  return <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{value}</p>;
}

function ReplyBubble({ reply }: { reply: TicketReply }) {
  const fromSupport = reply.author === 'support';
  return (
    <li className={fromSupport ? 'pr-4' : 'pl-4'}>
      <div
        className={[
          'rounded-xl px-3 py-2.5',
          fromSupport ? 'bg-white ring-1 ring-slate-200/90' : 'bg-action-surface',
        ].join(' ')}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold text-slate-600">
            {fromSupport ? 'Support' : 'You'}
          </span>
          <span className="text-[11px] text-slate-400">{formatDate(reply.at)}</span>
        </div>
        {reply.body.trim() ? <TicketBody value={reply.body} /> : null}
        <ScreenshotGallery shots={reply.screenshots ?? []} />
      </div>
    </li>
  );
}

function TicketDetail({
  ticket,
  onTicketUpdate,
}: {
  ticket: CockpitTicket;
  onTicketUpdate: (ticket: CockpitTicket) => void;
}) {
  const updates = ticket.status_updates ?? [];
  const priorityLabel = TICKET_PRIORITY_META[ticket.priority]?.label ?? ticket.priority;
  const description = ticket.description?.trim() ?? '';
  const screenshots = attachmentsNotInBody(
    description,
    ticket.screenshots ?? ticket.source_metadata?.screenshots ?? [],
  );
  const replies = ticket.replies ?? [];

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(ticket.replies == null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingThread(ticket.replies == null);
    setThreadError(null);
    void getTicket(ticket.external_ticket_id)
      .then(({ ticket: next }) => {
        if (!cancelled) onTicketUpdate(next);
      })
      .catch((err) => {
        if (!cancelled) {
          setThreadError(err instanceof Error ? err.message : 'Could not load the conversation');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticket.external_ticket_id, onTicketUpdate]);

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    const body = reply.trim();
    if (!body) {
      setReplyError('Write a reply before sending.');
      return;
    }
    setReplyError(null);
    setSending(true);
    try {
      const { ticket: next } = await replyToTicket({
        external_ticket_id: ticket.external_ticket_id,
        body,
      });
      setReply('');
      onTicketUpdate(next);
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : 'Could not send the reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-slate-200/80 bg-slate-50/70 px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="rounded-full border border-slate-200/90 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
          {priorityLabel} priority
        </span>
      </div>
      {description ? (
        <TicketBody value={description} />
      ) : (
        <p className="text-sm italic text-slate-500">No description</p>
      )}
      {screenshots.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attachments</p>
          <ScreenshotGallery shots={screenshots} />
        </div>
      )}

      <div className="space-y-2 border-t border-slate-200/80 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conversation</p>
        {loadingThread && (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Loading replies…
          </p>
        )}
        {threadError && (
          <p className="text-xs text-red-700" role="alert">{threadError}</p>
        )}
        {!loadingThread && replies.length === 0 && !threadError && (
          <p className="text-xs text-slate-500">No replies yet. Add an update below.</p>
        )}
        {replies.length > 0 && (
          <ol className="space-y-2">
            {replies.map((item) => (
              <ReplyBubble key={item.id} reply={item} />
            ))}
          </ol>
        )}
        <form className="space-y-2" onSubmit={(event) => void sendReply(event)}>
          <label htmlFor={`help-reply-${ticket.external_ticket_id}`} className="sr-only">
            Reply
          </label>
          <textarea
            id={`help-reply-${ticket.external_ticket_id}`}
            rows={3}
            maxLength={20000}
            value={reply}
            onChange={(event) => setReply(event.target.value)}
            placeholder="Reply to support…"
            className="w-full resize-y rounded-lg border border-slate-300/90 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50"
            disabled={sending}
          />
          {replyError && (
            <p className="text-xs text-red-700" role="alert">{replyError}</p>
          )}
          <button type="submit" className="btn-primary w-full" disabled={sending || !reply.trim()}>
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Sending…
              </>
            ) : (
              'Send reply'
            )}
          </button>
        </form>
      </div>

      {updates.length > 0 && (
        <ol className="space-y-2 border-t border-slate-200/80 pt-3">
          {updates.map((update, index) => (
            <li key={`${update.type}-${update.at}-${index}`} className="flex gap-2 text-xs text-slate-600">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" aria-hidden />
              <span>
                <span className="font-medium text-slate-800">
                  {update.type.replaceAll('_', ' ')}
                </span>
                {update.metadata && typeof update.metadata.to === 'string' ? (
                  <span> → {String(update.metadata.to).replaceAll('_', ' ')}</span>
                ) : null}
                <span className="text-slate-400"> · {formatDate(update.at)}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export function HelpTicketsPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const [tab, setTab] = useState<Tab>('new');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ticketType, setTicketType] = useState<TicketType>('issue');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [screenshots, setScreenshots] = useState<TicketScreenshotDraft[]>([]);
  const screenshotsRef = useRef<TicketScreenshotDraft[]>([]);
  const [screenshotBusy, setScreenshotBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const [tickets, setTickets] = useState<CockpitTicket[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setTicketType('issue');
    setPriority('medium');
    setFormError(null);
    revokeScreenshotUrls(screenshotsRef.current);
    screenshotsRef.current = [];
    setScreenshots([]);
  }, []);

  const addScreenshotFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setFormError(null);
    const room = MAX_TICKET_SCREENSHOTS - screenshotsRef.current.length;
    if (room <= 0) {
      setFormError(`You can attach up to ${MAX_TICKET_SCREENSHOTS} screenshots.`);
      return;
    }
    if (files.length > room) {
      setFormError(`Only ${room} more screenshot${room === 1 ? '' : 's'} can be added.`);
    }
    setScreenshotBusy(true);
    try {
      const added: TicketScreenshotDraft[] = [];
      for (const [index, file] of files.slice(0, room).entries()) {
        added.push(await fileToScreenshotDraft(file, screenshotsRef.current.length + index));
      }
      const next = [...screenshotsRef.current, ...added].slice(0, MAX_TICKET_SCREENSHOTS);
      screenshotsRef.current = next;
      setScreenshots(next);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not attach the screenshot');
    } finally {
      setScreenshotBusy(false);
    }
  }, []);

  const removeScreenshot = useCallback((id: string) => {
    const next = screenshotsRef.current.filter((shot) => shot.id !== id);
    revokeScreenshotUrls(screenshotsRef.current.filter((shot) => shot.id === id));
    screenshotsRef.current = next;
    setScreenshots(next);
  }, []);

  useEffect(() => {
    return () => revokeScreenshotUrls(screenshotsRef.current);
  }, []);

  useEffect(() => {
    if (!open || tab !== 'new') return;
    const onWindowPaste = (event: globalThis.ClipboardEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-ticket-details-editor]')) return;
      const files = collectImageFiles(event.clipboardData?.files);
      if (files.length === 0) return;
      event.preventDefault();
      void addScreenshotFiles(files);
    };
    window.addEventListener('paste', onWindowPaste);
    return () => window.removeEventListener('paste', onWindowPaste);
  }, [open, tab, addScreenshotFiles]);

  const onScreenshotInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = collectImageFiles(event.target.files);
    event.target.value = '';
    void addScreenshotFiles(files);
  };

  const onScreenshotDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void addScreenshotFiles(collectImageFiles(event.dataTransfer?.files));
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { tickets: rows } = await listTickets();
      setTickets(rows);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Could not load tickets');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const upsertTicket = useCallback((next: CockpitTicket) => {
    setTickets((prev) => {
      const index = prev.findIndex((row) => row.external_ticket_id === next.external_ticket_id);
      if (index < 0) return [next, ...prev];
      const copy = [...prev];
      copy[index] = { ...copy[index], ...next };
      return copy;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab('new');
    setFormSuccess(null);
    setSelectedId(null);
    resetForm();
    void loadHistory();
  }, [open, loadHistory, resetForm]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    if (!title.trim()) {
      setFormError('Add a short title so we know what this is about.');
      return;
    }

    setSubmitting(true);
    try {
      const { ticket } = await createTicket({
        title: title.trim(),
        ticket_type: ticketType,
        description: isEmptyTicketHtml(description) ? undefined : description,
        priority,
        page: `${location.pathname}${location.search}`,
        screenshots: screenshotsRef.current.map((shot) => shot.payload),
      });
      resetForm();
      setTickets((prev) => [ticket, ...prev.filter((row) => row.external_ticket_id !== ticket.external_ticket_id)]);
      setSelectedId(ticket.external_ticket_id);
      setFormSuccess('Ticket sent. You can follow it in History.');
      setTab('history');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create the ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const selected = tickets.find((ticket) => ticket.external_ticket_id === selectedId) ?? null;

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[1px]" aria-hidden />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel
          id="help-tickets-drawer"
          className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl ring-1 ring-slate-900/5"
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-200/90 px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-action-surface text-action">
                <LifeBuoy className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <DialogTitle className="text-base font-semibold tracking-tight text-slate-900">
                  Help & support
                </DialogTitle>
                <p className="mt-0.5 text-sm leading-snug text-slate-500">
                  Send a request, or follow one you’ve already submitted.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35"
              aria-label="Close help"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="border-b border-slate-200/90 px-5 py-3">
            <div className="flex rounded-lg bg-slate-100 p-0.5" role="tablist" aria-label="Support sections">
              {(
                [
                  ['new', 'New ticket'],
                  ['history', 'History'],
                ] as const
              ).map(([id, label]) => {
                const selectedTab = tab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    id={`help-tab-${id}`}
                    aria-selected={selectedTab}
                    aria-controls={`help-panel-${id}`}
                    tabIndex={selectedTab ? 0 : -1}
                    onClick={() => setTab(id)}
                    className={[
                      'relative flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
                      selectedTab
                        ? 'bg-action-surface text-action shadow-sm'
                        : 'text-slate-500 hover:text-slate-800',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-action/35',
                    ].join(' ')}
                  >
                    {label}
                    {id === 'history' && tickets.length > 0 && (
                      <span className="ml-1.5 text-xs font-medium tabular-nums opacity-70">
                        {tickets.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {tab === 'new' ? (
              <form
                id="help-panel-new"
                role="tabpanel"
                aria-labelledby="help-tab-new"
                className="space-y-5"
                onSubmit={handleSubmit}
              >
                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    What do you need?
                  </legend>
                  <div className="grid gap-2">
                    {TICKET_TYPES.map((type) => {
                      const meta = TICKET_TYPE_META[type];
                      const checked = ticketType === type;
                      return (
                        <label
                          key={type}
                          className={[
                            'flex cursor-pointer items-start gap-3 rounded-xl border px-3.5 py-3 transition-colors',
                            checked
                              ? 'border-action/40 bg-action-surface'
                              : 'border-slate-200/90 hover:border-slate-300 hover:bg-slate-50/70',
                            'focus-within:ring-2 focus-within:ring-action/35 focus-within:ring-offset-2',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                              checked ? 'border-action bg-action' : 'border-slate-300 bg-white',
                            ].join(' ')}
                            aria-hidden
                          >
                            {checked ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                          </span>
                          <input
                            type="radio"
                            name="ticket-type"
                            value={type}
                            checked={checked}
                            onChange={() => setTicketType(type)}
                            className="sr-only"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-slate-900">{meta.label}</span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
                              {meta.helper}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="help-ticket-title" className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Title
                  </label>
                  <input
                    id="help-ticket-title"
                    type="text"
                    maxLength={200}
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Short summary"
                    className="w-full rounded-lg border border-slate-300/90 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
                    disabled={submitting}
                    required
                  />
                </div>

                <div>
                  <p id="help-ticket-description-label" className="mb-1.5 text-xs font-semibold text-slate-600">
                    Details
                  </p>
                  <TicketDetailsEditor
                    value={description}
                    onChange={setDescription}
                    disabled={submitting}
                  />
                </div>

                <div>
                  <p className="mb-1.5 text-xs font-semibold text-slate-600">Attachments</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    multiple
                    className="sr-only"
                    onChange={onScreenshotInput}
                    disabled={submitting || screenshotBusy}
                  />
                  <div
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={onScreenshotDrop}
                    className="rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-3 py-3"
                  >
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={submitting || screenshotBusy || screenshots.length >= MAX_TICKET_SCREENSHOTS}
                      className="btn-secondary-sm w-full"
                    >
                      {screenshotBusy ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          Adding…
                        </>
                      ) : (
                        <>
                          <ImagePlus className="h-4 w-4" aria-hidden />
                          Add an attachment
                        </>
                      )}
                    </button>
                    <p className="mt-2 text-center text-[11px] text-slate-500">
                      Up to {MAX_TICKET_SCREENSHOTS} images, kept separate from the text. Paste or drop a file here.
                    </p>
                    {screenshots.length > 0 && (
                      <ul className="mt-3 grid grid-cols-2 gap-2">
                        {screenshots.map((shot) => (
                          <li key={shot.id} className="relative overflow-hidden rounded-lg border border-slate-200 bg-white">
                            <img src={shot.previewUrl} alt={shot.name} className="h-24 w-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeScreenshot(shot.id)}
                              className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow-sm hover:bg-white hover:text-slate-900"
                              aria-label={`Remove ${shot.name}`}
                              disabled={submitting}
                            >
                              <X className="h-3.5 w-3.5" aria-hidden />
                            </button>
                            <span className="block truncate px-2 py-1 text-[11px] text-slate-500">{shot.name}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="help-ticket-priority" className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Priority
                  </label>
                  <select
                    id="help-ticket-priority"
                    value={priority}
                    onChange={(event) => setPriority(event.target.value as TicketPriority)}
                    className="w-full rounded-lg border border-slate-300/90 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
                    disabled={submitting}
                  >
                    {TICKET_PRIORITIES.map((value) => (
                      <option key={value} value={value}>
                        {TICKET_PRIORITY_META[value].label}
                      </option>
                    ))}
                  </select>
                </div>

                {formError && (
                  <p
                    className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
                    role="alert"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{formError}</span>
                  </p>
                )}

                <button type="submit" className="btn-primary w-full" disabled={submitting || screenshotBusy}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" aria-hidden />
                      Send ticket
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div
                id="help-panel-history"
                role="tabpanel"
                aria-labelledby="help-tab-history"
                aria-busy={historyLoading}
                className="space-y-3"
              >
                {formSuccess && (
                  <p
                    className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800"
                    role="status"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{formSuccess}</span>
                  </p>
                )}
                {historyError && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
                    role="alert"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p>{historyError}</p>
                      <button type="button" className="btn-text-action mt-1.5 text-xs" onClick={() => void loadHistory()}>
                        Try again
                      </button>
                    </div>
                  </div>
                )}
                {historyLoading ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin text-action" aria-hidden />
                    Loading your tickets…
                  </div>
                ) : tickets.length === 0 && !historyError ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-10 text-center">
                    <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-action-surface text-action">
                      <LifeBuoy className="h-5 w-5" aria-hidden />
                    </span>
                    <p className="mt-3 text-sm font-semibold text-slate-800">No tickets yet</p>
                    <p className="mt-1 text-sm text-slate-500">Requests you send will show up here.</p>
                    <button type="button" className="btn-teal-outline mt-4" onClick={() => setTab('new')}>
                      Create a ticket
                    </button>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {tickets.map((ticket) => {
                      const isSelected = selectedId === ticket.external_ticket_id;
                      return (
                        <li
                          key={ticket.external_ticket_id}
                          className={[
                            'overflow-hidden rounded-xl border transition-colors',
                            isSelected
                              ? 'border-action/35 bg-white shadow-sm'
                              : 'border-slate-200/90 bg-white hover:border-slate-300',
                          ].join(' ')}
                        >
                          <HistoryItem
                            ticket={ticket}
                            selected={isSelected}
                            onSelect={() =>
                              setSelectedId((current) =>
                                current === ticket.external_ticket_id ? null : ticket.external_ticket_id,
                              )
                            }
                          />
                          {selected?.external_ticket_id === ticket.external_ticket_id && (
                            <TicketDetail ticket={selected} onTicketUpdate={upsertTicket} />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
